import React from 'react';
import {
  Bell,
  AlertTriangle,
  MessageSquare,
  Megaphone,
  FileCheck,
  FileX,
  Building2,
  Users,
  Cake,
  Dog,
  Hand,
  CheckCircle2,
  LogOut,
  Ticket,
  UserCheck,
  Info,
  MonitorSmartphone,
  Clock,
  Paperclip,
  AlarmClock,
  Download,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  IN_APP_NOTIFICATION_TYPE,
  SEVERITY_PRESENTATION,
  inAppAnnouncementOf,
  isInAppAnnouncementExpired,
} from '@/lib/notifications/inAppNotification';


export interface NotificationRowData {
  id: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  isRead: boolean;
  createdAt: string;
}

export const getNotificationIcon = (type: string): React.ComponentType<{ className?: string }> => {
  switch (type) {
    case 'CAMPAIGN_CREATED':
    case 'CAMPAIGN_COMPLETED':
    case 'CAMPAIGN_COMPLETED_WITH_ERRORS':
    case 'CAMPAIGN_SCHEDULED':
    case 'CAMPAIGN_STARTED':
    case 'CAMPAIGN_RETRY_FAILED':
      return Megaphone;
    case 'MESSAGE_RECEIVED':
    case 'chat_request':
    case 'TICKET_COMMENT':
      return MessageSquare;
    case 'TEMPLATE_APPROVED':
      return FileCheck;
    case 'TEMPLATE_REJECTED':
      return FileX;
    case 'BUSINESS_ACCOUNT_STATUS_CHANGE':
      return Building2;
    case 'TEAM_INVITATION':
      return Users;
    case 'cake_order_form':
    case 'cake_interest':
      return Cake;
    case 'treats_interest':
      return Dog;
    case 'customer_interaction':
      return Hand;
    case 'order_confirmed':
      return CheckCircle2;
    case 'session_ended':
      return LogOut;
    case 'TICKET_ASSIGNED':
      return Ticket;
    case 'CONVERSATION_ASSIGNED':
      return UserCheck;
    case 'SYSTEM_ALERT':
      return AlertTriangle;
    case 'SYSTEM_INFO':
      return Info;
    case 'AI_AGENT_NEEDS_ATTENTION':
      return MonitorSmartphone;
    case 'REMINDER':
      return AlarmClock;
    case 'CONTACT_EXPORT_COMPLETED':
      return Download;
    case IN_APP_NOTIFICATION_TYPE:
      return Megaphone;
    default:
      return Bell;
  }
};

export const getNotificationModule = (type: string): string | null => {
  switch (type) {
    case 'CAMPAIGN_CREATED':
    case 'CAMPAIGN_COMPLETED':
    case 'CAMPAIGN_COMPLETED_WITH_ERRORS':
    case 'CAMPAIGN_SCHEDULED':
    case 'CAMPAIGN_STARTED':
    case 'CAMPAIGN_RETRY_FAILED':
      return 'Campaigns';
    case 'MESSAGE_RECEIVED':
    case 'chat_request':
    case 'customer_interaction':
    case 'cake_order_form':
    case 'cake_interest':
    case 'treats_interest':
    case 'order_confirmed':
    case 'session_ended':
    case 'CONVERSATION_ASSIGNED':
      return 'Inbox';
    case 'TEMPLATE_APPROVED':
    case 'TEMPLATE_REJECTED':
      return 'Templates';
    case 'BUSINESS_ACCOUNT_STATUS_CHANGE':
      return 'Workspace';
    case 'TEAM_INVITATION':
      return 'Team';
    case 'TICKET_ASSIGNED':
    case 'TICKET_COMMENT':
      return 'Tickets';
    case 'AI_AGENT_NEEDS_ATTENTION':
      return 'AI agents';
    case 'REMINDER':
      return 'Reminders';
    case 'CONTACT_EXPORT_COMPLETED':
      return 'Contacts';
    case 'SYSTEM_NOTIFICATION':
    case 'SYSTEM_ALERT':
    case 'SYSTEM_INFO':
      return 'System';
    case IN_APP_NOTIFICATION_TYPE:
      return 'Announcement';
    default:
      return null;
  }
};

const ACTION_REQUIRED_TYPES = ['cake_order_form', 'chat_request', 'cake_interest'];

export const NotificationRow: React.FC<{
  notification: NotificationRowData;
  onClick: () => void;
  dense?: boolean;
}> = ({ notification, onClick, dense = false }) => {
  const Icon = getNotificationIcon(notification.type);
  const module = getNotificationModule(notification.type);
  const unread = !notification.isRead;
  const isAttentionAlert = notification.type === 'AI_AGENT_NEEDS_ATTENTION';
  const actionRequired = ACTION_REQUIRED_TYPES.includes(notification.type);

  const announcement = inAppAnnouncementOf(notification);
  const ticket =
    notification.type === 'TICKET_ASSIGNED' || notification.type === 'TICKET_COMMENT'
      ? (notification.data as any)
      : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'flex cursor-pointer gap-3 text-left transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none',
        dense ? 'items-center px-4 py-3' : 'items-start px-5 py-4',
        isAttentionAlert && 'shadow-[inset_3px_0_0_hsl(var(--warn-fill))]',
        isAttentionAlert && unread && 'bg-warn-bg/60'
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-text-strong">
        <Icon className="h-4 w-4" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <p
            className={cn(
              'min-w-0 font-wn-semibold leading-wn-snug text-text-strongest',
              dense ? 'truncate text-wn-sm' : 'text-wn-md'
            )}
          >
            {notification.title}
          </p>
          {!dense && actionRequired && (
            <span className="shrink-0 rounded-full bg-neg-bg px-1.5 py-0.5 text-wn-2xs font-wn-semibold text-neg-text">
              Action required
            </span>
          )}
          {!dense && isAttentionAlert && unread && (
            <span className="shrink-0 rounded-full bg-warn-bg px-1.5 py-0.5 text-wn-2xs font-wn-semibold text-warn-text">
              Action needed
            </span>
          )}
        </div>

        {!dense && notification.message && (
          <p className="mt-0.5 text-wn-md leading-wn-snug text-text-default">
            {notification.message}
          </p>
        )}

        {(() => {
          if (dense || !announcement) return null;
          const severity = SEVERITY_PRESENTATION[announcement.severity];
          const expired = isInAppAnnouncementExpired(announcement);
          const hasMedia = announcement.media.length > 0;
          if (announcement.severity === 'info' && !expired && !hasMedia) return null;
          return (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-wn-sm text-text-muted">
              {announcement.severity !== 'info' ? (
                <span
                  className={`rounded-full border px-1.5 py-0.5 text-wn-2xs font-wn-medium ${severity.chipClass}`}
                >
                  {severity.label}
                </span>
              ) : null}
              {expired ? (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Expired
                </span>
              ) : null}
              {hasMedia ? (
                <span className="flex items-center gap-1">
                  <Paperclip className="h-3 w-3" />
                  {announcement.media.length} attachment
                  {announcement.media.length === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
          );
        })()}

        {!dense && ticket && (ticket.ticketNumber || ticket.contactName || ticket.subject) && (
          <div className="mt-1.5 space-y-0.5 text-wn-sm">
            {ticket.ticketNumber && (
              <div className="flex gap-1">
                <span className="shrink-0 text-text-muted">ID/Num:</span>
                <span className="font-wn-medium text-text-strongest">T-{ticket.ticketNumber}</span>
              </div>
            )}
            {ticket.contactName && (
              <div className="flex gap-1">
                <span className="shrink-0 text-text-muted">Customer:</span>
                <span className="line-clamp-1 text-text-default">{ticket.contactName}</span>
              </div>
            )}
            {ticket.subject && (
              <div className="flex gap-1">
                <span className="shrink-0 text-text-muted">Description:</span>
                <span className="line-clamp-1 text-text-default">{ticket.subject}</span>
              </div>
            )}
          </div>
        )}

        <p className={cn('text-wn-sm text-text-muted', dense ? 'mt-0.5' : 'mt-1')}>
          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
          {module && (
            <>
              <span aria-hidden className="mx-1">
                ·
              </span>
              {module}
            </>
          )}
        </p>
      </div>

      {unread && (
        <span
          aria-label="Unread"
          className={cn('h-2 w-2 shrink-0 rounded-full bg-brand', !dense && 'mt-1.5')}
        />
      )}
    </div>
  );
};
